import React, { useEffect, useState } from 'react';
import { getDb, waitForFirebase } from '../services/firebase';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, getDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { Select, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Button, TextField, IconButton, Typography, Box, TableContainer, Switch, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

interface GroupManagerProps {
  onClose?: () => void;
  group?: any;
  open?: boolean;
}

const GroupManager: React.FC<GroupManagerProps> = ({ onClose, group, open = false }) => {
  const { user } = useAuth();
  const [groupList, setGroupList] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<any>(group || null);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [addInput, setAddInput] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [mode, setMode] = useState<'list' | 'detail'>(group ? 'detail' : 'list');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [editingName, setEditingName] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  console.log('GroupManager onClose:', !!onClose);

  // 取得我管理的群組
  useEffect(() => {
    if (!user?.email) return;
    const fetchGroups = async () => {
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        console.error('Firestore 未初始化');
        return;
      }
      const q = query(collection(db, 'groups'), where('admins', 'array-contains', user.email));
      const snap = await getDocs(q);
      setGroupList(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchGroups();
  }, [user]);

  // 若 group props 有變化，則自動切換到 detail 視圖
  useEffect(() => {
    if (group) {
      refreshGroups(group.id);
      setMode('detail');
    }
  }, [group]);

  const syncAdminsToMembers = (group: any) => {
    if (!group) return group;
    const adminSet = new Set(group.admins || []);
    const newMembers = (group.members || []).map((m: any) => ({
      ...m,
      isAdmin: adminSet.has(m.email)
    }));
    return { ...group, members: newMembers };
  };

  // 重新 fetch 最新群組列表
  const refreshGroups = async (selectGroupId?: string) => {
    if (!user?.email) return;
    await waitForFirebase();
    const db = getDb();
    if (!db) {
      console.error('Firestore 未初始化');
      return;
    }
    const q = query(collection(db, 'groups'), where('admins', 'array-contains', user.email));
    const snap = await getDocs(q);
    const groups = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setGroupList(groups);
    if (selectGroupId) {
      // 進入 detail 時 fetch 最新 group
      const groupDoc = await getDoc(doc(db, 'groups', selectGroupId));
      if (groupDoc.exists()) {
        setSelectedGroup(syncAdminsToMembers({ id: groupDoc.id, ...groupDoc.data() }));
      }
    }
  };

  // 選取群組
  const handleSelectGroup = async (groupId: string) => {
    await refreshGroups(groupId);
    setSelectedEmails([]);
  };

  // 勾選成員
  const handleCheckMember = (email: string, checked: boolean) => {
    setSelectedEmails(prev => checked ? [...prev, email] : prev.filter(e => e !== email));
  };

  // 設/取消管理員
  const handleToggleAdmin = async (email: string, checked: boolean) => {
    if (!selectedGroup) return;
    await waitForFirebase();
    const db = getDb();
    if (!db) {
      console.error('Firestore 未初始化');
      return;
    }
    const newMembers = selectedGroup.members.map((m: any) =>
      m.email === email ? { ...m, isAdmin: checked } : m
    );
    const newAdmins = newMembers.filter((m: any) => m.isAdmin).map((m: any) => m.email.toLowerCase()); // 🔧 修復：轉小寫
    await updateDoc(doc(db, 'groups', selectedGroup.id), {
      members: newMembers,
      admins: newAdmins,
      membersEmail: newMembers.map((m: any) => m.email.toLowerCase()) // 🔧 修復：轉小寫
    });
    await refreshGroups(selectedGroup.id);
  };

  // 刪除成員
  const handleDeleteMember = async (email: string) => {
    if (!selectedGroup) return;
    await waitForFirebase();
    const db = getDb();
    if (!db) {
      console.error('Firestore 未初始化');
      return;
    }
    const emailLower = email.toLowerCase(); // 🔧 修復：統一轉小寫比對
    const newMembers = selectedGroup.members.filter((m: any) => m.email.toLowerCase() !== emailLower); // 🔧 修復：轉小寫比對
    // admins 欄位同步移除該 email
    const newAdmins = (selectedGroup.admins || []).filter((adminEmail: string) => adminEmail.toLowerCase() !== emailLower); // 🔧 修復：轉小寫比對
    await updateDoc(doc(db, 'groups', selectedGroup.id), {
      members: newMembers,
      admins: newAdmins,
      membersEmail: newMembers.map((m: any) => m.email.toLowerCase()) // 🔧 修復：轉小寫
    });
    await refreshGroups(selectedGroup.id);
  };

  // 批次刪除
  const handleDeleteSelected = async () => {
    for (const email of selectedEmails) {
      await handleDeleteMember(email);
    }
    setSelectedEmails([]);
    await refreshGroups(selectedGroup.id);
  };

  // 新增好友
  const handleAddMembers = async () => {
    if (!selectedGroup || !user?.email) return;
    await waitForFirebase();
    const db = getDb();
    if (!db) {
      console.error('Firestore 未初始化');
      return;
    }
    const emails = addInput.split(/,|\n/).map(e => e.trim().toLowerCase()).filter(Boolean); // 🔧 修復：轉小寫
    let newMembers = [...selectedGroup.members];
    const existingEmails = newMembers.map((m: any) => m.email.toLowerCase()); // 🔧 修復：比對時也轉小寫
    const userEmailLower = user.email.toLowerCase(); // 🔧 修復：用戶 Email 轉小寫
    // 過濾掉自己的 email與已存在的 email
    const filteredEmails = emails.filter(email => email && !existingEmails.includes(email) && email !== userEmailLower);
    // 檢查 members 裡有沒有自己
    if (!newMembers.some((m: any) => m.email.toLowerCase() === userEmailLower)) { // 🔧 修復：比對時轉小寫
      newMembers = [
        { email: userEmailLower, name: '', isAdmin: true, profile: '', joinedAt: new Date(), status: '已通過' }, // 🔧 修復：存小寫
        ...newMembers
      ];
    }
    newMembers = [
      ...newMembers,
      ...filteredEmails.map(email => ({
        email, // 🔧 已經是小寫
        name: '',
        isAdmin: false,
        profile: '',
        joinedAt: new Date(),
        status: '已通過' // 管理員加入預設開啟
      }))
    ];
    // 再次去重，保證 email 唯一（比對時用小寫）
    const uniqueMembers = Array.from(new Map(newMembers.map(m => [m.email.toLowerCase(), { ...m, email: m.email.toLowerCase() }])).values()); // 🔧 修復：確保小寫
    await updateDoc(doc(db, 'groups', selectedGroup.id), {
      members: uniqueMembers,
      membersEmail: uniqueMembers.map((m: any) => m.email.toLowerCase()) // 🔧 修復：轉小寫
    });
    setAddInput('');
    await refreshGroups(selectedGroup.id);
  };

  // 批次發送 Email（呼叫後端 API）
  const handleSendEmailToSelected = async () => {
    await fetch('/api/send-group-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        emails: selectedEmails,
        message: emailMsg,
        groupName: selectedGroup.name
      })
    });
    alert('已發送');
    setEmailMsg('');
  };

  // 批次刪除群組
  const handleDeleteGroups = async () => {
    if (!selectedGroupIds.length) return;
    await waitForFirebase();
    const db = getDb();
    if (!db) {
      console.error('Firestore 未初始化');
      return;
    }
    let groupNames = groupList.filter(g => selectedGroupIds.includes(g.id)).map(g => g.name);
    let confirmMsg = '';
    if (groupNames.length === 1) {
      confirmMsg = `確定刪除「${groupNames[0]}」群組？\n（刪除後，組員將無法看群組影片內容）`;
    } else {
      confirmMsg = `確定刪除「${groupNames[0]}」等 ${groupNames.length} 個群組？\n（刪除後，組員將無法看群組影片內容）`;
    }
    if (!window.confirm(confirmMsg)) return;
    for (const groupId of selectedGroupIds) {
      await deleteDoc(doc(db, 'groups', groupId));
    }
    setGroupList(groupList.filter(g => !selectedGroupIds.includes(g.id)));
    setSelectedGroupIds([]);
  };

  // 編輯群組名稱
  const handleEditGroupName = () => {
    setEditingName(true);
    setNewGroupName(selectedGroup.name);
  };
  const handleSaveGroupName = async () => {
    if (!selectedGroup || !newGroupName.trim() || newGroupName === selectedGroup.name) {
      setEditingName(false);
      setNewGroupName(selectedGroup.name);
      return;
    }
    await waitForFirebase();
    const db = getDb();
    if (!db) {
      console.error('Firestore 未初始化');
      return;
    }
    await updateDoc(doc(db, 'groups', selectedGroup.id), { name: newGroupName.trim() });
    setEditingName(false);
    setNewGroupName('');
    await refreshGroups(selectedGroup.id);
  };

  // 新增切換群組權限的處理函式
  const handleToggleStatus = async (email: string, checked: boolean) => {
    if (!selectedGroup) return;
    await waitForFirebase();
    const db = getDb();
    if (!db) {
      console.error('Firestore 未初始化');
      return;
    }

    // 1. 更新群組文件中的成員狀態
    const newMembers = selectedGroup.members.map((m: any) =>
      m.email === email ? { ...m, status: checked ? '已通過' : '未核准' } : m
    );
    await updateDoc(doc(db, 'groups', selectedGroup.id), {
      members: newMembers,
    });

    // 2. 同步更新使用者文件中的群組列表
    try {
      // 透過 email 查詢使用者
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userRef = doc(db, 'users', userDoc.id);
        const groupId = selectedGroup.id;

        if (checked) {
          // 新增權限：將 groupId 加入到使用者的 groups 陣列
          await updateDoc(userRef, {
            groups: arrayUnion(groupId),
          });
        } else {
          // 移除權限：從使用者的 groups 陣列中移除 groupId
          await updateDoc(userRef, {
            groups: arrayRemove(groupId),
          });
        }
      } else {
        console.warn(`在 "users" 集合中找不到 email 為 ${email} 的使用者`);
      }
    } catch (error) {
      console.error('同步使用者群組列表失敗:', error);
    }

    await refreshGroups(selectedGroup.id);
  };

  const content = (
    <Box sx={{ width: '100%', maxWidth: '100%', p: 3, minHeight: 400, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {mode === 'list' && (
        <>
          <Typography variant="h5" fontWeight={700} mb={2}>Hello！管理員，您要管理哪個群組？</Typography>
          <TableContainer sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 20 }}><Checkbox /></TableCell>
                  <TableCell sx={{ minWidth: 140 }}>群組名稱</TableCell>
                  <TableCell sx={{ minWidth: 40, textAlign: 'center' }} align="center">調整</TableCell>
                  <TableCell sx={{ minWidth: 60, textAlign: 'center' }} align="center">人數</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>管理員ID</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>群組創始人</TableCell>
                  <TableCell sx={{ minWidth: 90 }}>建立日期</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupList.map(g => (
                  <TableRow key={g.id}>
                    <TableCell sx={{ minWidth: 20 }}>
                      <Checkbox checked={selectedGroupIds.includes(g.id)} onChange={e => {
                        setSelectedGroupIds(val => e.target.checked ? [...val, g.id] : val.filter(id => id !== g.id));
                      }} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>{g.name}</TableCell>
                    <TableCell sx={{ minWidth: 40, textAlign: 'center' }} align="center">
                      <Button onClick={() => { setSelectedGroup(g); setMode('detail'); }}>成員</Button>
                    </TableCell>
                    <TableCell sx={{ minWidth: 60, textAlign: 'center' }} align="center">{g.members?.length || 0}</TableCell>
                    <TableCell sx={{ minWidth: 120, whiteSpace: 'pre-line' }}>{(g.admins || []).join('\n')}</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>{g.creator || (g.admins && g.admins[0]) || '-'}</TableCell>
                    <TableCell sx={{ minWidth: 90 }}>
                      {g.createdAt?.toDate
                        ? g.createdAt.toDate().toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '/')
                        : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="contained"
            color="error"
            fullWidth
            onClick={handleDeleteGroups}
            disabled={!selectedGroupIds.length}
            sx={{ mt: 2, fontWeight: 700 }}
          >
            刪除群組
          </Button>
        </>
      )}
      {mode === 'detail' && selectedGroup && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {editingName ? (
              <>
                <TextField
                  value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                  size="small"
                  sx={{ flex: 1, mr: 1, maxWidth: 340 }}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSaveGroupName();
                    if (e.key === 'Escape') {
                      setEditingName(false);
                      setNewGroupName(selectedGroup.name);
                    }
                  }}
                />
                <IconButton onClick={handleSaveGroupName}>
                  <SaveIcon />
                </IconButton>
                <IconButton onClick={() => { setEditingName(false); setNewGroupName(selectedGroup.name); }}>
                  <CloseIcon />
                </IconButton>
              </>
            ) : (
              <>
                <Typography variant="h5" sx={{ textAlign: 'left', fontWeight: 700, fontSize: 28, letterSpacing: 1, color: '#1976d2', mr: 1 }}>
                  《{selectedGroup.name}》
                </Typography>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleEditGroupName}
                  sx={{ minWidth: 0, px: 1, fontSize: 10 }}
                >
                  Edit
                </Button>
              </>
            )}
          </Box>
          <Button
            onClick={() => setMode('list')}
            variant="contained"
            color="primary"
            sx={{ mb: 2, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
          >
            返回群組列表
          </Button>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell><Checkbox /></TableCell>
                <TableCell>Email</TableCell>
                <TableCell>名稱</TableCell>
                <TableCell>管理員</TableCell>
                <TableCell>群組權限</TableCell>
                <TableCell>最後登入</TableCell>
                <TableCell>個人資料</TableCell>
                <TableCell>刪除</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {Array.from(new Map((selectedGroup.members ?? []).map((m: any) => [m.email, m])).values()).map((m: any, idx: number) => (
                  <TableRow key={m.email + idx}>
                    <TableCell>
                      <Checkbox checked={selectedEmails.includes(m.email)} onChange={e => handleCheckMember(m.email, e.target.checked)} />
                    </TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>{m.name}</TableCell>
                    <TableCell>
                      <Checkbox checked={m.isAdmin} onChange={e => handleToggleAdmin(m.email, e.target.checked)} />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={m.isAdmin || m.status === '已通過'}
                        color="primary"
                        onChange={e => handleToggleStatus(m.email, e.target.checked)}
                        disabled={!!m.isAdmin}
                      />
                    </TableCell>
                    <TableCell>
                      {m.lastLogin || '-'}
                    </TableCell>
                    <TableCell>{m.profile}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleDeleteMember(m.email)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            </TableBody>
          </Table>
          <Button
            onClick={handleDeleteSelected}
            disabled={!selectedEmails.length}
            variant="contained"
            color="error"
            sx={{ mb: 2 }}
          >
            刪除選取
          </Button>
          <Box sx={{ mb: 2 }}>
            <TextField
              label="新增好友（多筆請用逗號或換行分隔）"
              multiline
              rows={2}
              value={addInput}
              onChange={e => setAddInput(e.target.value)}
              sx={{ mr: 2, width: 320 }}
            />
            <Button onClick={handleAddMembers} variant="contained">新增好友</Button>
          </Box>
          <Box>
            <TextField
              label="發送訊息給勾選好友"
              multiline
              rows={2}
              value={emailMsg}
              onChange={e => setEmailMsg(e.target.value)}
              sx={{ mr: 2, width: 320 }}
            />
            <Button onClick={handleSendEmailToSelected} disabled={!selectedEmails.length || !emailMsg} variant="contained">發送Email</Button>
          </Box>
        </>
      )}
      <Box sx={{ flex: 1 }} />
    </Box>
  );

  if (open) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">群組管理</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {content}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>關閉</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return content;
};

export default GroupManager; 